import { mailboxRankChecker, type MailboxInfo } from 'owa-client-types';
import { getCloudEnvironmentForEmailAddress } from '../utils/getCloudEnvironmentForEmailAddress';
import type { CloudEnvironmentInfo } from '../types/CloudEnvironmentInfo';
import { CloudEnvironmentInfoImpl } from '../utils/CloudEnvironmentInfoImpl';
import type { CloudEnvironment } from '../types/CloudEnvironment';
import { pluggableAccountFunctions } from './pluggableAccountFunctions';
import { getApplicationEnvironment } from './getApplicationEnvironment';

export async function getCloudEnvironmentForMailboxInfo(
    mailboxInfo: MailboxInfo
): Promise<CloudEnvironmentInfo> {
    // Resolve the account's cloud environment and get the application environment (from the native
    // host, at the point we actually need it) in parallel, then hand both to the info object so
    // buildOutlookUrl can align same-cloud accounts on the application origin.
    const [cloudEnv, applicationEnvironment] = await Promise.all([
        getCloudEnvironmentForMailboxInfoInternal(mailboxInfo),
        // Best-effort origin-alignment optimization: a native-host failure must not fail cloud
        // resolution, so degrade to undefined. (getApplicationEnvironment already logs the error.)
        getApplicationEnvironment().catch(() => undefined),
    ]);
    return new CloudEnvironmentInfoImpl(cloudEnv, applicationEnvironment);
}

export async function getCloudEnvironmentForMailboxInfoInternal(
    mailboxInfo: MailboxInfo
): Promise<CloudEnvironment> {
    if (pluggableAccountFunctions && !mailboxRankChecker.isCoprincipal(mailboxInfo)) {
        // When given a resource account, try to look up the coprincipal account instead; the coprincipal lookup
        // should hit the cache (since discovery + caching should have been done when that account was created)
        // and coprincipal accounts + resource accounts will be in the same cloud environment
        mailboxInfo =
            pluggableAccountFunctions.getCoprincipalMailboxInfoForMailboxInfo(mailboxInfo) ??
            mailboxInfo;
    }

    // MailboxInfo defines the auth context (userIdentity) and the target resource (mailboxSmtpAddress) for a request;
    // we look up the cloud environment for the target resource since that's where we're trying to go
    return getCloudEnvironmentForEmailAddress(mailboxInfo.mailboxSmtpAddress);
}
